import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Flex, Text, Button, Badge, HStack, VStack, IconButton, Input, Textarea,
  FormControl, FormLabel, useToast, Spinner, Code, Divider, Switch, Tooltip,
  Tabs, TabList, TabPanels, Tab, TabPanel, Alert, AlertIcon,
} from '@chakra-ui/react';
import {
  FiPlus, FiTrash2, FiCopy, FiExternalLink, FiGlobe, FiRefreshCw, FiEdit2,
  FiCheck, FiInfo,
} from 'react-icons/fi';
import api from '../api';

const ORIGIN = window.location.origin.replace('app.', 'www.'); // or just use the full api URL

// Common field names from formmail.php forms on forgebos.com
const COMMON_FIELDS = [
  { key: 'name',          label: 'Full Name',        standard: 'contact_name' },
  { key: 'email',         label: 'Email Address',     standard: 'email' },
  { key: 'phone',         label: 'Phone Number',      standard: 'phone' },
  { key: 'company',       label: 'Company Name',      standard: 'company_name' },
  { key: 'business_type', label: 'Business Type',     standard: 'extra_notes' },
  { key: 'staff_count',   label: 'Number of Staff',   standard: 'extra_notes' },
  { key: 'turnover',      label: 'Annual Turnover',   standard: 'extra_notes' },
  { key: 'message',       label: 'Message',           standard: 'extra_notes' },
];

export default function WebFormsModal({ campaignId, campaignName }) {
  const [forms, setForms]         = useState([]);
  const [loading, setLoading]     = useState(false);
  const [creating, setCreating]   = useState(false);
  const [editing, setEditing]     = useState(null);
  const [form, setForm]           = useState({
    title: '', description: '', redirect_url: '', notes_template: '',
  });
  const toast = useToast();

  const fetchForms = useCallback(async () => {
    if (!campaignId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/web-forms?campaign_id=${campaignId}`);
      setForms(data || []);
    } catch(e) {}
    setLoading(false);
  }, [campaignId]);

  useEffect(() => { fetchForms(); }, [fetchForms]);

  const handleCreate = async () => {
    try {
      const { data } = await api.post('/web-forms', { ...form, campaign_id: campaignId });
      setForms(prev => [data, ...prev]);
      setCreating(false);
      setForm({ title:'', description:'', redirect_url:'', notes_template:'' });
      toast({ title:'Web form created', status:'success', duration:2000 });
    } catch(e) { toast({ title:'Failed to create', status:'error', duration:2000 }); }
  };

  const handleUpdate = async (token) => {
    try {
      const { data } = await api.put(`/web-forms/${token}`, form);
      setForms(prev => prev.map(f => f.token === token ? data : f));
      setEditing(null);
      toast({ title:'Updated', status:'success', duration:1500 });
    } catch(e) { toast({ title:'Update failed', status:'error', duration:2000 }); }
  };

  const handleDelete = async (token) => {
    await api.delete(`/web-forms/${token}`);
    setForms(prev => prev.filter(f => f.token !== token));
  };

  const handleToggle = async (wf) => {
    const { data } = await api.put(`/web-forms/${wf.token}`, { is_active: !wf.is_active });
    setForms(prev => prev.map(f => f.token === wf.token ? data : f));
  };

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    toast({ title: `${label} copied`, status:'success', duration:1500 });
  };

  const submitUrl = (token) => `${window.location.origin}/api/web-forms/submit/${token}`;

  // JSX variables instead of inner function components to avoid focus loss on keystroke
  const newFormEditorJSX = (
    <Box p={4} border="1px" borderColor="blue.200" borderRadius="md" bg="blue.50">
      <Text fontSize="13px" fontWeight="700" mb={3} color="blue.700">+ New Web Lead Form</Text>
      <VStack spacing={3} align="stretch">
        <FormControl>
          <FormLabel fontSize="xs" mb={0.5}>Form Title (internal reference)</FormLabel>
          <Input size="sm" value={form.title} onChange={e => setForm(p => ({...p, title: e.target.value}))}
            placeholder="e.g. Website Get Started Form" />
        </FormControl>
        <FormControl>
          <FormLabel fontSize="xs" mb={0.5}>Description (optional)</FormLabel>
          <Input size="sm" value={form.description} onChange={e => setForm(p => ({...p, description: e.target.value}))}
            placeholder="Where is this form used?" />
        </FormControl>
        <FormControl>
          <FormLabel fontSize="xs" mb={0.5}>Redirect URL after submission</FormLabel>
          <Input size="sm" value={form.redirect_url} onChange={e => setForm(p => ({...p, redirect_url: e.target.value}))}
            placeholder="https://www.forgebos.com/thank-you.html" />
          <Text fontSize="10px" color="gray.400" mt={0.5}>Leave blank to use default thank you page. For JSON/AJAX, no redirect is needed.</Text>
        </FormControl>
        <HStack justify="flex-end">
          <Button size="sm" variant="ghost" onClick={() => { setCreating(false); setEditing(null); }}>Cancel</Button>
          <Button size="sm" colorScheme="blue" leftIcon={<FiCheck size={13} />} onClick={handleCreate}>
            Create Form
          </Button>
        </HStack>
      </VStack>
    </Box>
  );

  const editFormEditorJSX = (wfToken, wfTitle) => (
    <Box p={4} border="1px" borderColor="orange.200" borderRadius="md" bg="orange.50">
      <Text fontSize="13px" fontWeight="700" mb={3} color="orange.700">Edit: {wfTitle}</Text>
      <VStack spacing={3} align="stretch">
        <FormControl>
          <FormLabel fontSize="xs" mb={0.5}>Form Title (internal reference)</FormLabel>
          <Input size="sm" value={form.title} onChange={e => setForm(p => ({...p, title: e.target.value}))}
            placeholder="e.g. Website Get Started Form" />
        </FormControl>
        <FormControl>
          <FormLabel fontSize="xs" mb={0.5}>Description (optional)</FormLabel>
          <Input size="sm" value={form.description} onChange={e => setForm(p => ({...p, description: e.target.value}))}
            placeholder="Where is this form used?" />
        </FormControl>
        <FormControl>
          <FormLabel fontSize="xs" mb={0.5}>Redirect URL after submission</FormLabel>
          <Input size="sm" value={form.redirect_url} onChange={e => setForm(p => ({...p, redirect_url: e.target.value}))}
            placeholder="https://www.forgebos.com/thank-you.html" />
          <Text fontSize="10px" color="gray.400" mt={0.5}>Leave blank to use default thank you page. For JSON/AJAX, no redirect is needed.</Text>
        </FormControl>
        <HStack justify="flex-end">
          <Button size="sm" variant="ghost" onClick={() => { setCreating(false); setEditing(null); }}>Cancel</Button>
          <Button size="sm" colorScheme="orange" leftIcon={<FiCheck size={13} />} onClick={() => handleUpdate(wfToken)}>
            Save Changes
          </Button>
        </HStack>
      </VStack>
    </Box>
  );

  return (
    <VStack spacing={3} align="stretch">
      <Flex justify="space-between" align="center">
        <VStack align="flex-start" spacing={0}>
          <Text fontSize="13px" fontWeight="700" color="gray.700">Website Lead Forms</Text>
          <Text fontSize="11px" color="gray.400">Replace formmail.php with a direct ForgeBOS prospect link</Text>
        </VStack>
        <HStack>
          <IconButton icon={<FiRefreshCw size={12} />} size="xs" variant="ghost" onClick={fetchForms} />
          <Button size="sm" leftIcon={<FiPlus size={13} />} colorScheme="blue"
            onClick={() => { setForm({ title:'', description:'', redirect_url:'', notes_template:'' }); setCreating(true); setEditing(null); }}>
            New Web Form
          </Button>
        </HStack>
      </Flex>

      {creating && newFormEditorJSX}

      {loading && <Spinner size="sm" color="blue.400" mx="auto" my={4} />}

      {!loading && forms.length === 0 && !creating && (
        <Box textAlign="center" py={10}>
          <Box fontSize="40px" mb={3}>🌐</Box>
          <Text fontSize="13px" color="gray.500" fontWeight="600">No web forms yet</Text>
          <Text fontSize="11px" color="gray.400" mt={1} mb={4}>
            Create a form to replace formmail.php on your website.<br/>
            Point your HTML form's <Code fontSize="10px">action</Code> attribute to the generated URL.
          </Text>
          <Button leftIcon={<FiPlus />} colorScheme="blue" size="sm"
            onClick={() => setCreating(true)}>Create First Form</Button>
        </Box>
      )}

      {forms.map(wf => (
        <Box key={wf.token} border="1px" borderColor={wf.is_active ? 'blue.200' : 'gray.200'}
          borderRadius="lg" bg="white" overflow="hidden"
          opacity={wf.is_active ? 1 : 0.6}>
          <Flex px={4} py={3} bg={wf.is_active ? 'blue.50' : 'gray.50'} align="center" justify="space-between"
            borderBottom="1px" borderColor={wf.is_active ? 'blue.100' : 'gray.200'}>
            <HStack spacing={2}>
              <FiGlobe size={14} color={wf.is_active ? '#3182CE' : '#9CA3AF'} />
              <Text fontSize="13px" fontWeight="700" color={wf.is_active ? 'blue.800' : 'gray.400'}>{wf.title}</Text>
              <Badge colorScheme={wf.is_active ? 'blue' : 'gray'} fontSize="8px">
                {wf.is_active ? 'Active' : 'Inactive'}
              </Badge>
            </HStack>
            <HStack spacing={1}>
              <Tooltip label={wf.is_active ? 'Deactivate' : 'Activate'} fontSize="xs">
                <Switch size="sm" isChecked={!!wf.is_active} onChange={() => handleToggle(wf)} colorScheme="blue" />
              </Tooltip>
              <IconButton icon={<FiEdit2 size={12} />} size="xs" variant="ghost"
                onClick={() => { setEditing(wf.token); setEditing(wf.token); setForm({ title: wf.title, description: wf.description||'', redirect_url: wf.redirect_url||'', notes_template: wf.notes_template||'' }); setCreating(false); }} />
              <IconButton icon={<FiTrash2 size={12} />} size="xs" variant="ghost" colorScheme="red"
                onClick={() => handleDelete(wf.token)} />
            </HStack>
          </Flex>

          <Box px={4} py={3}>
            {wf.description && <Text fontSize="11px" color="gray.500" mb={3}>{wf.description}</Text>}

            {editing === wf.token && (
              <Box mb={3}>
                {editFormEditorJSX(wf.token, wf.title)}
              </Box>
            )}

            <Tabs size="xs" colorScheme="blue">
              <TabList>
                <Tab fontSize="11px">Form Action URL</Tab>
                <Tab fontSize="11px">HTML Snippet</Tab>
                <Tab fontSize="11px">How to Use</Tab>
              </TabList>
              <TabPanels>
                {/* Action URL */}
                <TabPanel px={0} py={2}>
                  <Text fontSize="10px" color="gray.500" mb={1}>
                    Set this as your HTML form's <Code fontSize="9px">action</Code> attribute:
                  </Text>
                  <Flex align="center" gap={2}>
                    <Code fontSize="10px" p={2} borderRadius="md" flex={1} bg="gray.50" wordBreak="break-all">
                      {submitUrl(wf.token)}
                    </Code>
                    <IconButton icon={<FiCopy size={13} />} size="sm" colorScheme="blue" variant="outline"
                      onClick={() => copyToClipboard(submitUrl(wf.token), 'Form action URL')} />
                  </Flex>
                  {wf.redirect_url && (
                    <Text fontSize="10px" color="gray.400" mt={1}>
                      ↳ Redirects to: {wf.redirect_url}
                    </Text>
                  )}
                </TabPanel>

                {/* HTML snippet */}
                <TabPanel px={0} py={2}>
                  <Text fontSize="10px" color="gray.500" mb={1}>
                    Replace <Code fontSize="9px">action="scriptsies/formmail.php"</Code> with this:
                  </Text>
                  <Box position="relative">
                    <Code display="block" fontSize="9px" p={3} borderRadius="md" bg="gray.800" color="green.300"
                      whiteSpace="pre" overflowX="auto">
{`<form action="${submitUrl(wf.token)}" method="post" accept-charset="utf-8">
  <!-- Remove old formmail hidden fields (recipient, sort, alias, etc.) -->
  <!-- Keep all your existing visible fields as-is -->
  <!-- Optionally add: -->
  <input type="hidden" name="redirect" value="${wf.redirect_url || 'https://www.forgebos.com/thank-you.html'}">
</form>`}
                    </Code>
                    <IconButton icon={<FiCopy size={12} />} size="xs" variant="ghost" color="gray.400"
                      position="absolute" top={1} right={1}
                      onClick={() => copyToClipboard(
`<form action="${submitUrl(wf.token)}" method="post" accept-charset="utf-8">`,
                        'Form tag')} />
                  </Box>
                </TabPanel>

                {/* How to use */}
                <TabPanel px={0} py={2}>
                  <VStack spacing={2} align="stretch" fontSize="11px" color="gray.600">
                    <HStack align="flex-start" spacing={2}>
                      <Badge colorScheme="blue" flexShrink={0} mt={0.5}>1</Badge>
                      <Text>Open your website HTML and find the form with <Code fontSize="9px">action="scriptsies/formmail.php"</Code></Text>
                    </HStack>
                    <HStack align="flex-start" spacing={2}>
                      <Badge colorScheme="blue" flexShrink={0} mt={0.5}>2</Badge>
                      <Text>Replace the <Code fontSize="9px">action</Code> value with the URL above. Keep all your existing fields exactly as they are.</Text>
                    </HStack>
                    <HStack align="flex-start" spacing={2}>
                      <Badge colorScheme="blue" flexShrink={0} mt={0.5}>3</Badge>
                      <Text>Remove the old formmail hidden fields (<Code fontSize="9px">recipient</Code>, <Code fontSize="9px">subject</Code>, <Code fontSize="9px">sort</Code>, <Code fontSize="9px">alias</Code> etc.)</Text>
                    </HStack>
                    <HStack align="flex-start" spacing={2}>
                      <Badge colorScheme="blue" flexShrink={0} mt={0.5}>4</Badge>
                      <Text>Optionally add <Code fontSize="9px">&lt;input type="hidden" name="redirect" value="your-thank-you-url"&gt;</Code></Text>
                    </HStack>
                    <HStack align="flex-start" spacing={2}>
                      <Badge colorScheme="blue" flexShrink={0} mt={0.5}>5</Badge>
                      <Text>ForgeBOS auto-maps: <Code fontSize="9px">name</Code> → contact, <Code fontSize="9px">email</Code> → email, <Code fontSize="9px">company</Code> → company, <Code fontSize="9px">phone</Code> → phone. Other fields go into the prospect notes.</Text>
                    </HStack>
                    <Alert status="info" size="sm" borderRadius="md" py={2} mt={1}>
                      <AlertIcon boxSize={3} />
                      <Text fontSize="10px">Submissions create a new prospect entry in the <strong>{campaignName}</strong> campaign automatically.</Text>
                    </Alert>
                  </VStack>
                </TabPanel>
              </TabPanels>
            </Tabs>
          </Box>
        </Box>
      ))}
    </VStack>
  );
}
